import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSendOtpMutation, useVerifyOtpMutation } from "../services/apiSlice";
import { useAuth } from "../context/AuthContext";
import { 
  Smartphone, Lock, ArrowRight, Shield, 
  CheckCircle, AlertCircle, Loader2, 
  RefreshCw, Mail, KeyRound, User,
  SmartphoneIcon, MessageSquare
} from 'lucide-react';

const Login = ({ setIsLoggedIn, setIsAdmin }) => {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isInactive, setIsInactive] = useState(false);

  const navigate = useNavigate();
  const { handleLogin } = useAuth();
  const [sendOtp] = useSendOtpMutation();
  const [verifyOtp] = useVerifyOtpMutation();

  // Handle OTP resend cooldown
  React.useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSendOtp = async () => {
    if (!phone.trim()) {
      setError("Please enter a phone number");
      return;
    }
    if (phone.length !== 10) {
      setError("Phone number must be 10 digits");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await sendOtp({ phone }).unwrap();

      // Check for error response first
      if (res?.Result === "Phone not found" || res?.Result === "Phone not found." || res?.Message?.includes("not found")) {
        setOtpSent(false);
        setError("Phone number not found in our system. Please register first.");
        return;
      }

      // Get OTP from response
      if (res?.Result && res.Result !== "Phone not found" && res.Result !== "Phone not found.") {
        setGeneratedOtp(res.Result);
        setOtpSent(true);
        setError("");
        setResendCooldown(60); // Start cooldown timer
      } else if (res?.resultSet?.otpCode) {
        setGeneratedOtp(res.resultSet.otpCode);
        setOtpSent(true);
        setError("");
        setResendCooldown(60);
      } else if (res?.Success === false || res?.IsSuccess === false) {
        setOtpSent(false);
        const errorMsg = res?.Message || res?.Result || "Failed to send OTP. Please try again.";
        setError(errorMsg);
      } else {
        setOtpSent(true);
        setError("");
        setResendCooldown(60);
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      const errorMsg = err?.data?.message || err?.data?.Result || "Failed to send OTP. Please try again.";
      setOtpSent(false);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError("Please enter the OTP");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await verifyOtp({ phone, otpCode: otp }).unwrap();

      // Check for ResultSet from backend
      if (res?.ResultSet?.token && res?.ResultSet?.user) {
        const { token, user } = res.ResultSet;

        // Check if user account is active
        if (user.status && user.status.toLowerCase() === 'inactive') {
          setError("Your account is Inactive. Please contact the admin");
          setIsInactive(true);
          setLoading(false);
          return;
        }

        // Use AuthContext to handle login
        handleLogin(token, user);

        // Also update props for backward compatibility
        setIsLoggedIn(true);
        const isUserAdmin = user?.roleName?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "admin";
        setIsAdmin(isUserAdmin);

        // Navigate based on user role
        if (isUserAdmin) {
          navigate("/admin/dashboard");
        } else {
          navigate("/dashboard");
        }
      } else {
        setError("Invalid response from server. Please try again.");
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
      const errorMsg = err?.data?.message || err?.data?.Result || "Invalid OTP. Please try again.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = () => {
    if (resendCooldown > 0) return;
    setOtp("");
    setOtpSent(false);
    setGeneratedOtp(null);
    setError("");
    setResendCooldown(0);
    setIsInactive(false);
  };

  const formatPhoneNumber = (phone) => {
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Brand/Info */}
          <div className="hidden lg:block">
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-10 text-white shadow-2xl">
              <div className="mb-10">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <Shield className="w-8 h-8" />
                </div>
                <h1 className="text-4xl font-bold mb-4">Welcome Back</h1>
                <p className="text-blue-100 text-lg">
                  Access your Smart Lend account to manage loans, track repayments, and get personalized insights.
                </p>
              </div>

              <div className="space-y-6 mt-12">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SmartphoneIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Secure Login</h3>
                    <p className="text-blue-200 text-sm">Two-factor authentication via SMS for enhanced security</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Instant Access</h3>
                    <p className="text-blue-200 text-sm">Get immediate access to your dashboard and loan details</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">24/7 Support</h3>
                    <p className="text-blue-200 text-sm">Our team is always ready to assist you with any queries</p>
                  </div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="mt-12 pt-8 border-t border-white/20">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Bank-Level Security</p>
                    <p className="text-sm text-blue-200">Your data is encrypted and protected</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div>
            <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-xl max-w-md mx-auto">
              {/* Header */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mb-6 shadow-lg">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Login to Smart Lend</h2>
                <p className="text-gray-600">Enter your phone number to continue</p>
              </div>

              {!otpSent ? (
                /* Phone Input Screen */
                <div className="space-y-6">
                  {/* Error Message */}
                  {error && (
                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-900">Error</p>
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Phone Input */}
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Smartphone className="w-4 h-4" />
                      Phone Number
                    </label>
                    <div className="relative">
                      <input
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="0712345678"
                        maxLength={10}
                        type="tel"
                        disabled={loading}
                        className={`w-full pl-12 pr-4 py-4 bg-gray-50 border-2 ${
                          error && !otpSent ? 'border-red-300' : 'border-gray-200'
                        } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                      />
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <Smartphone className="w-5 h-5" />
                      </div>
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                        {phone.length}/10
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Enter your 10-digit registered phone number</p>
                  </div>

                  {/* Get OTP Button */}
                  <button
                    onClick={handleSendOtp}
                    disabled={loading || phone.length !== 10}
                    className={`w-full group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                      loading || phone.length !== 10 ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending OTP...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Send Verification Code
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    )}
                  </button>

                  {/* Divider */}
                  <div className="relative flex items-center justify-center">
                    <div className="flex-grow border-t border-gray-200"></div>
                    <span className="flex-shrink mx-4 text-gray-500 text-sm">or</span>
                    <div className="flex-grow border-t border-gray-200"></div>
                  </div>

                  {/* Alternative Login */}
                  <div className="space-y-3">
                    <button
                      disabled
                      className="w-full px-6 py-3 bg-white text-gray-700 font-medium rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-3 opacity-50 cursor-not-allowed"
                    >
                      <Mail className="w-5 h-5" />
                      Login with Email
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">Soon</span>
                    </button>
                  </div>

                  {/* Register Link */}
                  <div className="text-center pt-6 border-t border-gray-200">
                    <p className="text-gray-600">
                      Don't have an account?{' '}
                      <Link 
                        to="/register" 
                        className="text-blue-600 font-semibold hover:text-blue-700 transition-colors inline-flex items-center gap-1"
                      >
                        Create account
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </p>
                  </div>
                </div>
              ) : (
                /* OTP Verification Screen */
                <div className="space-y-6">
                  {/* Success Message */}
                  <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-2xl">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-900">OTP Sent Successfully!</p>
                      <p className="text-sm text-green-700">Verification code sent to {formatPhoneNumber(phone)}</p>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-900">Error</p>
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Phone Info */}
                  <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Verifying</p>
                        <p className="text-lg font-semibold text-gray-900">{formatPhoneNumber(phone)}</p>
                      </div>
                      <button 
                        onClick={handleResendOtp}
                        disabled={resendCooldown > 0}
                        className="text-sm text-blue-600 font-medium hover:text-blue-700 transition-colors flex items-center gap-1"
                      >
                        <RefreshCw className={`w-4 h-4 ${resendCooldown > 0 ? 'animate-spin' : ''}`} />
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Change Number'}
                      </button>
                    </div>
                  </div>

                  {/* OTP Input */}
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <KeyRound className="w-4 h-4" />
                      Enter Verification Code
                    </label>
                    <div className="relative">
                      <input
                        id="otp"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        type="text"
                        disabled={loading}
                        className="w-full pl-12 pr-4 py-2 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all text-center text-lg tracking-widest"
                      />
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <Lock className="w-5 h-5" />
                      </div>
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                        {otp.length}/6
                      </div>
                    </div>
                    
                    {/* OTP Display for Testing */}
                    {/* {generatedOtp && (
                      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-sm text-amber-800 font-medium mb-1">Test OTP (For Development)</p>
                        <div className="flex items-center justify-between">
                          <code className="text-2xl font-bold text-amber-900">{generatedOtp}</code>
                          <button
                            onClick={() => navigator.clipboard.writeText(generatedOtp)}
                            className="text-sm text-amber-600 hover:text-amber-700"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    )} */}

                    {/* Resend OTP */}
                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-sm text-gray-600">Didn't receive the code?</p>
                      <button
                        onClick={() => {
                          setResendCooldown(60);
                          handleSendOtp();
                        }}
                        disabled={resendCooldown > 0 || loading}
                        className="text-sm text-blue-600 font-medium hover:text-blue-700 transition-colors flex items-center gap-1"
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Verify Button */}
                  <button
                    onClick={handleVerifyOtp}
                    disabled={loading || otp.length !== 6 || isInactive}
                    className={`w-full group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                      loading || otp.length !== 6 || isInactive ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Verifying...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Login to Dashboard
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    )}
                  </button>

                  {/* Back to Phone Input */}
                  <div className="text-center">
                    <button
                      onClick={handleResendOtp}
                      className="text-gray-600 hover:text-gray-800 transition-colors text-sm font-medium"
                    >
                      ← Use different phone number
                    </button>
                  </div>
                </div>
              )}

              {/* Security Note */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">
                      Your security is our priority. We use bank-level encryption to protect your data.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile View Info Panel */}
            <div className="lg:hidden mt-8 bg-white rounded-3xl border border-gray-200 p-6 shadow-xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">SMS Verification</h3>
                  <p className="text-sm text-gray-600">We'll send you a 6-digit code via SMS</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">10s</div>
                  <div className="text-xs text-gray-500">OTP Delivery</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">5m</div>
                  <div className="text-xs text-gray-500">OTP Valid</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">24/7</div>
                  <div className="text-xs text-gray-500">Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>By logging in, you agree to our <a href="#" className="text-blue-600 hover:underline">Terms of Service</a> and <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a></p>
          <p className="mt-2">Need help? <a href="mailto:support@smartlend.com" className="text-blue-600 hover:underline">support@smartlend.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default Login;